'use client';

import { useEffect, useRef } from 'react';
import { useClerk, useSignIn, useSignUp } from '@clerk/nextjs';
import { LoadingMorph } from '@extern/ui/loading-morph';
import { useSessionStorage } from '@uidotdev/usehooks';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { useSignUpQ1Variant } from '@/app/(auth)/_hooks/use-sign-up-q1-variant';
import { persistLastAuthenticationStrategy } from '@/app/(auth)/_utils/auth-last-authentication-strategy';
import { resetStaleOAuthState } from '@/app/(auth)/_utils/reset-stale-oauth-state';
import { useSignInFlowStore } from '@/app/(auth)/sign-in/_hooks/use-sign-in-flow-store';
import { resolveSecondFactorStrategy } from '@/app/(auth)/sign-in/_utils/resolve-second-factor-strategy';
import { useSignUpFlowStore } from '@/app/(auth)/sign-up/_hooks/use-sign-up-flow-store';
import { completeSignUp } from '@/app/(auth)/sign-up/_utils/complete-sign-up';
import { resolvePortfolioAttribution } from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import { useUtmParams } from '@/hooks/use-utm-params';
import { AUTH_ERROR_QUERY_KEY, AUTH_ERROR_QUERY_VALUE_SSO_FAILED, DEFAULT_LOGGED_PAGE, SIGNUP_ACCOUNT_CREATION_METHOD_KEY, SIGNUP_NEWSLETTER_KEY } from '@/infra/constants/routes';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { ErrorTracking } from '@/lib/error-tracking';
import { useGetFeatureFlag } from '@/lib/feature-flags/hooks/use-get-feature-flag';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { resolvePostAuthRedirectUrl, resolvePostSignUpRedirectUrl } from '@/utils/resolve-post-sign-up-redirect-url';
function buildSsoFailedUrl(path: string): string {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(AUTH_ERROR_QUERY_KEY, AUTH_ERROR_QUERY_VALUE_SSO_FAILED);
  return `${url.pathname}${url.search}`;
}
async function redirectSsoFailed(router: ReturnType<typeof useRouter>, path: string, signIn: ReturnType<typeof useSignIn>['signIn'], signUp: ReturnType<typeof useSignUp>['signUp'], clerkClient: ReturnType<typeof useClerk>['client']): Promise<void> {
  await resetStaleOAuthState({
    signIn,
    signUp,
    clerkClient
  });
  router.push(buildSsoFailedUrl(path));
}
export function SSOCallback() {
  const router = useRouter();
  const [newsletter, setNewsletter] = useSessionStorage<boolean | null>(SIGNUP_NEWSLETTER_KEY, null);
  const [, setAccountCreationMethod] = useSessionStorage<'email' | 'google' | null>(SIGNUP_ACCOUNT_CREATION_METHOD_KEY, null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSignUpCallback = pathname?.includes('/sign-up/sso-callback');
  const {
    campaign,
    content,
    medium,
    source,
    referrer,
    partner,
    portfolioCode,
    portfolioUrl,
    ttclid,
    gclid,
    fbclid,
    msclkid,
    ttp
  } = useUtmParams();
  const allowlistedParams = getAllowlistedSearchParams(searchParams, {
    ttclid,
    gclid,
    fbclid,
    msclkid,
    ttp
  });
  const portfolioAttribution = resolvePortfolioAttribution({
    portfolioCode,
    portfolioUrl,
    utmContent: content,
    referrer: null
  });
  const {
    trackSsoSignInCompleted,
    trackSsoSignUpCompleted,
    trackSsoTransferUsed,
    trackSsoExistingSessionReused,
    trackSsoFallbackToSignIn,
    trackSsoFallbackToSignUp,
    trackAuthError,
    identifyAuthUser
  } = useAuthEvents();
  const clerk = useClerk();
  const {
    signIn
  } = useSignIn();
  const {
    signUp
  } = useSignUp();
  const hasRun = useRef(false);
  const hasActivatedSignUpSession = useRef(false);
  const {
    track
  } = useAnalytics();
  const freemiumVariant = useGetFeatureFlag('ab_test_freemium');
  const {
    variant: signUpQ1Variant
  } = useSignUpQ1Variant();
  const handleSignInComplete = (session: {
    currentTask?: unknown;
    user?: {
      id?: string;
      emailAddresses?: {
        emailAddress?: string;
      }[];
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  }) => {
    useSignInFlowStore.getState().onChange({
      step: 'start'
    });
    if (session?.currentTask) {
      return;
    }
    identifyAuthUser({
      userId: session?.user?.id,
      email: session?.user?.emailAddresses?.[0]?.emailAddress,
      firstName: session?.user?.firstName,
      lastName: session?.user?.lastName,
      accountCreationMethod: 'google'
    });
    trackSsoSignInCompleted();
    persistLastAuthenticationStrategy('oauth_google');
    router.push(resolvePostAuthRedirectUrl(searchParams, DEFAULT_LOGGED_PAGE));
  };
  const mirrorSignUpMetadataToClerk = async (): Promise<void> => {
    const mirrored: Record<string, unknown> = {};
    if (partner) {
      mirrored.partner = partner;
    }
    if (portfolioAttribution) {
      mirrored.portfolio_attribution = portfolioAttribution;
    }
    if (freemiumVariant !== undefined) {
      mirrored.ab_test_freemium = freemiumVariant;
    }
    if (signUpQ1Variant !== undefined) {
      mirrored.ab_test_sign_up_2026_q1 = signUpQ1Variant;
    }
    if (Object.keys(mirrored).length === 0 || !clerk.user) {
      return;
    }
    try {
      await clerk.user.update({
        unsafeMetadata: {
          ...clerk.user.unsafeMetadata,
          ...mirrored
        }
      });
    } catch (error) {
      trackAuthError('sign_up', 'sso_callback', 'sso_mirror_unsafe_metadata_failed', {
        error_message: error instanceof Error ? error.message : String(error)
      });
      ErrorTracking.captureMessage('sso_mirror_unsafe_metadata_failed', {
        tags: {
          auth_flow: 'sign_up',
          auth_step: 'sso_callback',
          error_code: 'sso_mirror_unsafe_metadata_failed'
        },
        extra: {
          error_message: error instanceof Error ? error.message : String(error)
        }
      });
    }
  };
  const handleSignUpComplete = async (session_0: {
    currentTask?: unknown;
    getToken: () => Promise<string | null>;
    reload: () => Promise<unknown>;
    user?: {
      id?: string;
      emailAddresses?: {
        emailAddress?: string;
      }[];
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  }) => {
    useSignUpFlowStore.getState().onChange({
      step: 'start'
    });
    if (session_0?.currentTask) {
      return;
    }
    await mirrorSignUpMetadataToClerk();
    await completeSignUp({
      userId: session_0?.user?.id ?? '',
      email: session_0?.user?.emailAddresses?.[0]?.emailAddress ?? '',
      firstName: session_0?.user?.firstName ?? '',
      lastName: session_0?.user?.lastName ?? '',
      newsletter: newsletter ?? false,
      accountCreationMethod: 'google',
      allowlistedParams,
      campaign,
      medium,
      source,
      referrer,
      partner,
      portfolioAttribution,
      freemiumVariant,
      signUpQ1Variant,
      tracking: {
        identifyAuthUser,
        track,
        onCompleted: () => {
          trackSsoSignUpCompleted();
          persistLastAuthenticationStrategy('oauth_google');
        }
      },
      clearStoredNewsletter: () => setNewsletter(null),
      clearStoredAccountCreationMethod: () => setAccountCreationMethod(null),
      redirect: () => {
        router.push(resolvePostSignUpRedirectUrl(searchParams));
      }
    });
  };
  const finalizeSignIn = async (): Promise<void> => {
    await signIn.finalize({
      navigate: ({
        session: session_1
      }) => handleSignInComplete(session_1)
    });
  };
  const finalizeAndCompleteSignUp = async (): Promise<void> => {
    hasActivatedSignUpSession.current = true;
    let signUpFinalizeSession: Parameters<typeof handleSignUpComplete>[0] | null = null;
    await signUp.finalize({
      navigate: ({
        session: session_2
      }) => {
        signUpFinalizeSession = session_2;
      }
    });
    if (signUpFinalizeSession) {
      await handleSignUpComplete(signUpFinalizeSession);
    }
  };
  const failToSignIn = async (errorCode: string): Promise<void> => {
    trackSsoFallbackToSignIn();
    trackAuthError('sign_in', 'sso_callback', errorCode);
    await redirectSsoFailed(router, '/sign-in', signIn, signUp, clerk.client);
  };
  const failToSignUp = async (errorCode_0: string): Promise<void> => {
    trackSsoFallbackToSignUp();
    trackAuthError('sign_up', 'sso_callback', errorCode_0);
    await redirectSsoFailed(router, '/sign-up', signIn, signUp, clerk.client);
  };
  const isUnsupportedFirstFactor = (): boolean => signIn.status === 'needs_first_factor' && !signIn.supportedFirstFactors?.every(f => f.strategy === 'enterprise_sso');
  const handleUnsupportedFirstFactor = async (): Promise<void> => {
    if (isSignUpCallback) {
      await failToSignUp('sso_first_factor_not_supported');
      return;
    }
    await failToSignIn('sso_first_factor_not_supported');
  };
  const transferToSignIn = async (): Promise<void> => {
    trackSsoTransferUsed('sign_up_to_sign_in');
    await signIn.create({
      transfer: true
    });
    if (String(signIn.status) === 'complete') {
      await finalizeSignIn();
      return;
    }
    await failToSignIn('sso_fallback_sign_in');
  };
  const transferToSignUp = async (): Promise<void> => {
    trackSsoTransferUsed('sign_in_to_sign_up');
    await signUp.create({
      transfer: true
    });
    if (signUp.status === 'complete') {
      await finalizeAndCompleteSignUp();
      return;
    }
    await failToSignUp('sso_fallback_sign_up');
  };
  const handleMissingRequirements = async (): Promise<void> => {
    if (isSignUpCallback) {
      await failToSignUp('sso_missing_requirements_fallback');
      return;
    }
    await failToSignIn('sso_missing_requirements_fallback');
  };
  const reuseExistingSession = async (): Promise<void> => {
    const sessionId = signIn.existingSession?.sessionId || signUp.existingSession?.sessionId;
    if (!sessionId) {
      return;
    }
    if (isSignUpCallback) {
      useSignUpFlowStore.getState().onChange({
        step: 'choose'
      });
      const chooseUrl = allowlistedParams ? `/sign-up?step=choose&${allowlistedParams}` : '/sign-up?step=choose';
      router.push(chooseUrl);
      return;
    }
    trackSsoExistingSessionReused();
    await clerk.setActive({
      session: sessionId,
      navigate: ({
        session: session_3
      }) => {
        useSignInFlowStore.getState().onChange({
          step: 'start'
        });
        if (session_3?.currentTask) {
          return;
        }
        persistLastAuthenticationStrategy('oauth_google');
        router.push(resolvePostAuthRedirectUrl(searchParams, '/'));
      }
    });
  };
  const hasMissingRequirements = (): boolean => signIn.status === 'needs_identifier' && signUp.status === 'missing_requirements';
  const needsSecondFactorVerification = (): boolean => signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust';
  const needsSignInFallbackFactor = (): boolean => signIn.status === 'needs_new_password';
  const buildSignInVerificationsUrl = (): string => {
    const url = new URL('/sign-in', window.location.origin);
    url.searchParams.set('step', 'verifications');
    if (allowlistedParams) {
      for (const [key, value] of new URLSearchParams(allowlistedParams)) {
        url.searchParams.set(key, value);
      }
    }
    return `${url.pathname}${url.search}`;
  };
  const redirectToSignInSecondFactor = async (): Promise<void> => {
    const secondFactorStrategy = await resolveSecondFactorStrategy(signIn);
    if (!secondFactorStrategy) {
      await failToSignIn('sso_second_factor_not_available');
      return;
    }
    useSignInFlowStore.getState().onChange({
      step: 'verifications',
      secondFactorStrategy,
      firstFactorMethod: 'google',
      identifier: signIn.identifier ?? ''
    });
    router.push(buildSignInVerificationsUrl());
  };
  const hasReusableExistingSession = (): boolean => Boolean(signIn.existingSession || signUp.existingSession);
  const handleUnhandledSsoState = async (): Promise<void> => {
    const flow = isSignUpCallback ? 'sign_up' : 'sign_in';
    const fallbackPath = isSignUpCallback ? '/sign-up' : '/sign-in';
    trackAuthError(flow, 'sso_callback', 'sso_unhandled_state');
    await redirectSsoFailed(router, fallbackPath, signIn, signUp, clerk.client);
  };
  const reportSsoError = async (err: unknown): Promise<void> => {
    const flow_0 = isSignUpCallback ? 'sign_up' : 'sign_in';
    trackAuthError(flow_0, 'sso_callback', 'sso_unhandled_error', {
      error_message: err instanceof Error ? err.message : String(err)
    });
    ErrorTracking.captureMessage('sso_unhandled_error', {
      tags: {
        auth_flow: flow_0,
        auth_step: 'sso_callback',
        error_code: 'sso_unhandled_error'
      },
      extra: {
        error_message: err instanceof Error ? err.message : String(err)
      }
    });
    const fallbackUrl = isSignUpCallback || hasActivatedSignUpSession.current ? '/sign-up' : '/sign-in';
    const redirectUrl = allowlistedParams ? `${fallbackUrl}?${allowlistedParams}` : fallbackUrl;
    await redirectSsoFailed(router, redirectUrl, signIn, signUp, clerk.client);
  };
  const resolveSsoCallback = async (): Promise<void> => {
    hasActivatedSignUpSession.current = false;
    try {
      if (signIn.status === 'complete') {
        await finalizeSignIn();
        return;
      }
      if (signUp.isTransferable) {
        await transferToSignIn();
        return;
      }
      if (isUnsupportedFirstFactor()) {
        await handleUnsupportedFirstFactor();
        return;
      }
      if (signIn.isTransferable) {
        await transferToSignUp();
        return;
      }
      if (hasMissingRequirements()) {
        await handleMissingRequirements();
        return;
      }
      if (signUp.status === 'complete') {
        await finalizeAndCompleteSignUp();
        return;
      }
      if (needsSecondFactorVerification()) {
        await redirectToSignInSecondFactor();
        return;
      }
      if (needsSignInFallbackFactor()) {
        await failToSignIn('sso_needs_new_password');
        return;
      }
      if (hasReusableExistingSession()) {
        await reuseExistingSession();
        return;
      }
      await handleUnhandledSsoState();
    } catch (err_0) {
      await reportSsoError(err_0);
    }
  };
  const resolveSsoCallbackRef = useRef(resolveSsoCallback);
  resolveSsoCallbackRef.current = resolveSsoCallback;
  useEffect(() => {
    if (!clerk.loaded || hasRun.current) {
      return;
    }
    hasRun.current = true;
    resolveSsoCallbackRef.current();
  }, [clerk.loaded]);
  return <div className="flex min-h-screen items-center justify-center">
      <LoadingMorph />
      <div id="clerk-captcha" />
    </div>;
}